import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BarChart, Bar, Cell, XAxis, ResponsiveContainer, LabelList, Tooltip } from 'recharts';
import { CLINICUI_PATIENT } from '../constants/clinicui';
import Sidebar from '../components/layout/Sidebar';
import { usePermissions } from '../hooks/usePermissions';
import { PatientCardLink } from '../components/PatientCardLink';
import { dashboardApi } from '../services/api';
import {
  DrillDownData,
  PatientRecord,
  ClinicId,
  ClinicData,
} from '../components/widgets/PatientStatus/types';
import { getClinicConfig, CLINIC_PALETTE, WAITING_APPROVAL_CLINIC_ID } from '../components/widgets/PatientStatus/clinicPalette';
import ClinicPills from '../components/widgets/PatientStatus/parts/ClinicPills';
import '../components/widgets/PatientStatus/styles.css';
import './PatientStatusDrill.css';
import { today } from '../utils/date';

// Normalize JS's negative-zero (Math.round(-0.4) === -0) before formatting.
// Without this, small-negative averages render as "-0" in the stats row.
const fmt = (n: number) => (n === 0 ? 0 : n).toLocaleString('en-US');

// The drill table shows an extra "status" column on every (stage, side).
// Source differs per side:
//   - Stage 1 right (waiting for approval) → raw `status` (Waiting-for-parent2-sign, etc.)
//   - everywhere else → `invitation_status` (invitation N sent, missing slots, …)
// The header is named accordingly. Values are shown verbatim (English from DB).
type StatusFieldKey = 'status' | 'invitation_status';
const STATUS_COLUMN_CONFIG: Record<string, { field: StatusFieldKey; header: string }> = {
  'new|right':         { field: 'status',            header: 'סטטוס' },
  'new|left':          { field: 'invitation_status', header: 'סטטוס הזמנה' },
  'intake_cm|right':   { field: 'invitation_status', header: 'סטטוס הזמנה' },
  'intake_cm|left':    { field: 'invitation_status', header: 'סטטוס הזמנה' },
  'intake_psy|right':  { field: 'invitation_status', header: 'סטטוס הזמנה' },
  'intake_psy|left':   { field: 'invitation_status', header: 'סטטוס הזמנה' },
  'followup_cm|right': { field: 'invitation_status', header: 'סטטוס הזמנה' },
  'followup_cm|left':  { field: 'invitation_status', header: 'סטטוס הזמנה' },
  'followup_psy|right':{ field: 'invitation_status', header: 'סטטוס הזמנה' },
  'followup_psy|left': { field: 'invitation_status', header: 'סטטוס הזמנה' },
};
const EMPTY_STATUS_LABEL = '(ללא)';

const getRowStatus = (p: PatientRecord, field: StatusFieldKey): string => {
  const v = p[field];
  return v && v.trim() ? v : EMPTY_STATUS_LABEL;
};

// The provider panel + table column is now a TOGGLE between CM and PSY views.
// Each stage has a *default* primary provider, but the user can switch via a
// segmented control in the panel header. Stage 1 is included — transferred
// patients (source=move_*) keep their previous CM/PSY assignments and they
// only show up if the panel is rendered there.
type ProviderFieldKey = 'cm_name' | 'psy_name';
type ProviderView = 'cm' | 'psy';
const PROVIDER_VIEW_CONFIG: Record<ProviderView, { field: ProviderFieldKey; header: string }> = {
  cm:  { field: 'cm_name',  header: 'מנהל תיק' },
  psy: { field: 'psy_name', header: 'פסיכיאטר' },
};
const PROVIDER_DEFAULT_BY_STAGE: Record<string, ProviderView> = {
  new:          'cm',
  intake_cm:    'cm',
  intake_psy:   'psy',
  followup_cm:  'cm',
  followup_psy: 'psy',
};
const getDefaultProviderView = (stage: string): ProviderView =>
  PROVIDER_DEFAULT_BY_STAGE[stage] ?? 'cm';
const EMPTY_PROVIDER_LABEL = '(טרם שויך)';

const getProvider = (p: PatientRecord, field: ProviderFieldKey): string => {
  const v = p[field];
  return v && v.trim() ? v.trim() : EMPTY_PROVIDER_LABEL;
};

// Reformat backend ISO date (YYYY-MM-DD) to DD/MM/YYYY for Israeli display.
// Used for both intake date (created_date) and scheduled meeting date.
const formatDateDmy = (iso: string): string => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

// Today's local date in YYYY-MM-DD for lexicographic comparison with the ISO string.
const todayIsoLocal = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Deep-link target for clicking a patient in the drill table — opens the
// patient's page in the Clinic Management UI in a new tab. The `id` query
// param is the internal numeric id (= patient_base.ID).
const CLINICUI_PATIENT_URL = CLINICUI_PATIENT;

const HISTOGRAM_BUCKETS: { label: string; min: number; max: number }[] = [
  { label: '0-15',    min: 0,   max: 16 },
  { label: '16-30',   min: 16,  max: 31 },
  { label: '31-60',   min: 31,  max: 61 },
  { label: '61-90',   min: 61,  max: 91 },
  { label: '91-180',  min: 91,  max: 181 },
  { label: '181-365', min: 181, max: 366 },
  { label: '366+',    min: 366, max: Infinity },
];

// Past-meeting bucket — only relevant on LEFT side of stages 2-5, where
// the waiting_time can be negative (meeting date has passed, no status set).
// See backend CASE in DRILL_SQL_TEMPLATE for the data source.
const OVERDUE_BUCKET = { label: 'פגישה עברה', min: -Infinity, max: 0 };

// Bucket labels in the dropdown / pill carry " ימים" suffix, except the
// special overdue bucket whose label is already a full phrase.
const formatBucketLabel = (label: string) =>
  label === OVERDUE_BUCKET.label ? label : `${label} ימים`;

function median(arr: number[]): number {
  if (!arr.length) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
}

type SortKey = 'waiting_time' | 'created_date' | 'hm_id' | 'clinic_group' | 'next_meeting_date' | 'provider' | 'status';
type OpenDropdown = 'clinic' | 'time' | 'status' | 'provider' | null;

const PAGE_SIZE = 10;

export default function PatientStatusDrill() {
  const canOpenPatient = usePermissions().can('patient_card');
  const { stage = '', side = '' } = useParams<{ stage: string; side: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<DrillDownData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [selectedClinics, setSelectedClinics] = useState<Set<ClinicId> | null>(null);
  const [selectedBucket, setSelectedBucket] = useState<string | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string> | null>(null);
  const [selectedProviders, setSelectedProviders] = useState<Set<string> | null>(null);
  const [selectedProviderView, setSelectedProviderView] = useState<ProviderView>(() => getDefaultProviderView(stage));
  const [redFlagOnly, setRedFlagOnly] = useState(false);

  const statusColumn = STATUS_COLUMN_CONFIG[`${stage}|${side}`];
  // providerColumn is always defined now — the toggle picks which view.
  const providerColumn = PROVIDER_VIEW_CONFIG[selectedProviderView];
  // Meeting date is only meaningful on the LEFT side (next_meeting_scheduled=TRUE).
  // Stage 1 left ("approved, waiting for intake") is excluded: its time axis is
  // "days since approval", and 100% of those patients have a stale intake date —
  // the alarm would be uninformative.
  const showMeetingDate = side === 'left' && stage !== 'new';
  // Same contexts where waiting_time can be negative (overdue scheduled meetings).
  // Prepends the "פגישה עברה" bucket to the histogram + time-filter dropdown.
  const buckets = useMemo(
    () => (showMeetingDate ? [OVERDUE_BUCKET, ...HISTOGRAM_BUCKETS] : HISTOGRAM_BUCKETS),
    [showMeetingDate],
  );
  const todayIso = useMemo(() => todayIsoLocal(), []);
  const [openDropdown, setOpenDropdown] = useState<OpenDropdown>(null);
  const [sortKey, setSortKey] = useState<SortKey>('waiting_time');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [showAllClicked, setShowAllClicked] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);

  const filterBarRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!stage || !side) return;
    setData(null);
    setError(null);
    dashboardApi
      .getPatientStatusDrill(stage, side)
      .then((res) => {
        const raw: DrillDownData = res.data;
        // Reclassify waiting-approval patients (Clalit psy follow-up blocked on
        // HMO auth) as a virtual clinic. A single click on that pill cascades
        // through pills/histogram/breakdown/list — same machinery as a real
        // clinic, no extra filter state needed.
        const d: DrillDownData = {
          ...raw,
          patients: raw.patients.map((p) =>
            p.is_waiting_approval
              ? { ...p, clinic_group: WAITING_APPROVAL_CLINIC_ID }
              : p,
          ),
        };
        setData(d);
        const clinicsHere = Array.from(new Set(d.patients.map((p) => p.clinic_group)));
        setSelectedClinics(new Set(clinicsHere));
        // Initialize the status filter to "everything present here"
        const cfg = STATUS_COLUMN_CONFIG[`${stage}|${side}`];
        if (cfg) {
          const statusesHere = Array.from(new Set(d.patients.map((p) => getRowStatus(p, cfg.field))));
          setSelectedStatuses(new Set(statusesHere));
        } else {
          setSelectedStatuses(null);
        }
        // Reset provider view to the stage's default, then seed the filter from it
        const defaultView = getDefaultProviderView(stage);
        setSelectedProviderView(defaultView);
        const provField = PROVIDER_VIEW_CONFIG[defaultView].field;
        const providersHere = Array.from(new Set(d.patients.map((p) => getProvider(p, provField))));
        setSelectedProviders(new Set(providersHere));
      })
      .catch((e) => setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load'));
  }, [stage, side]);

  // Switching the provider view (cm ↔ psy) invalidates the existing selectedProviders
  // (different field, different names). Reset to "all values of the new view".
  const handleSwitchProviderView = (view: ProviderView) => {
    if (view === selectedProviderView) return;
    setSelectedProviderView(view);
    if (!data) return;
    const field = PROVIDER_VIEW_CONFIG[view].field;
    const valuesHere = Array.from(new Set(data.patients.map((p) => getProvider(p, field))));
    setSelectedProviders(new Set(valuesHere));
  };

  // Close any open dropdown when clicking outside the filter bar
  useEffect(() => {
    if (!openDropdown) return;
    const onDocClick = (e: MouseEvent) => {
      if (filterBarRef.current && !filterBarRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openDropdown]);

  // Stable clinic order for the page (palette order, filtered to clinics present in data)
  const clinicsInData = useMemo<ClinicId[]>(() => {
    if (!data) return [];
    const present = new Set(data.patients.map((p) => p.clinic_group));
    return CLINIC_PALETTE.map((c) => c.id).filter((id) => present.has(id));
  }, [data]);

  // Stable "all values" for provider + status — used by the filter-active checks
  // and the bar-click reset. Computed from the full dataset (no cross-filter),
  // mirroring how clinicsInData stays stable. Without this, reset goes to the
  // cross-filtered subset and "filter active" can flicker between true/false in
  // edge cases (e.g. cross-filter narrowing changes providerOptions length).
  const allProvidersInData = useMemo<string[]>(() => {
    if (!data || !providerColumn) return [];
    return Array.from(new Set(data.patients.map((p) => getProvider(p, providerColumn.field))));
  }, [data, providerColumn]);
  const allStatusValuesInData = useMemo<string[]>(() => {
    if (!data || !statusColumn) return [];
    return Array.from(new Set(data.patients.map((p) => getRowStatus(p, statusColumn.field))));
  }, [data, statusColumn]);

  // Cross-filter helper: apply every active filter to the patient set, with
  // optional "skip" flags so each panel can exclude its own filter from the
  // computation. Standard BI cross-filter pattern (Tableau / Power BI):
  // - The patient list = all filters applied.
  // - Each breakdown panel = all filters applied EXCEPT its own (so the
  //   selected bar stays visible while the panel's other bars dim/update
  //   to reflect the cross-section selected elsewhere).
  type FilterSkip = {
    skipClinic?: boolean; skipBucket?: boolean; skipStatus?: boolean;
    skipProvider?: boolean; skipRedFlag?: boolean;
  };
  const applyFilters = (patients: PatientRecord[], opts: FilterSkip = {}): PatientRecord[] => {
    let list = patients;
    if (!opts.skipClinic && selectedClinics) {
      list = list.filter((p) => selectedClinics.has(p.clinic_group));
    }
    if (!opts.skipBucket && selectedBucket) {
      const bucket = buckets.find((b) => b.label === selectedBucket);
      if (bucket) {
        list = list.filter((p) => p.waiting_time >= bucket.min && p.waiting_time < bucket.max);
      }
    }
    if (!opts.skipStatus && statusColumn && selectedStatuses) {
      list = list.filter((p) => selectedStatuses.has(getRowStatus(p, statusColumn.field)));
    }
    if (!opts.skipProvider && providerColumn && selectedProviders) {
      list = list.filter((p) => selectedProviders.has(getProvider(p, providerColumn.field)));
    }
    if (!opts.skipRedFlag && redFlagOnly) {
      list = list.filter((p) => p.red_flag);
    }
    return list;
  };

  // Patients with every active filter applied — drives the patient list + the
  // top stats. (Each panel uses its own "skip self" derived set below.)
  const filteredAll = useMemo<PatientRecord[]>(() => {
    if (!data) return [];
    return applyFilters(data.patients);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedClinics, selectedBucket, selectedStatuses, statusColumn, selectedProviders, providerColumn, redFlagOnly, buckets]);

  // Per-panel derived sets — all filters EXCEPT the panel's own.
  const dataForHistogram = useMemo<PatientRecord[]>(() => {
    if (!data) return [];
    return applyFilters(data.patients, { skipBucket: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedClinics, selectedStatuses, statusColumn, selectedProviders, providerColumn, redFlagOnly]);

  const dataForClinic = useMemo<PatientRecord[]>(() => {
    if (!data) return [];
    return applyFilters(data.patients, { skipClinic: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedBucket, selectedStatuses, statusColumn, selectedProviders, providerColumn, redFlagOnly, buckets]);

  const dataForStatus = useMemo<PatientRecord[]>(() => {
    if (!data) return [];
    return applyFilters(data.patients, { skipStatus: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedClinics, selectedBucket, selectedProviders, providerColumn, redFlagOnly, buckets]);

  const dataForProvider = useMemo<PatientRecord[]>(() => {
    if (!data) return [];
    return applyFilters(data.patients, { skipProvider: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedClinics, selectedBucket, selectedStatuses, statusColumn, redFlagOnly, buckets]);

  const dataForRedFlag = useMemo<PatientRecord[]>(() => {
    if (!data) return [];
    return applyFilters(data.patients, { skipRedFlag: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedClinics, selectedBucket, selectedStatuses, statusColumn, selectedProviders, providerColumn, buckets]);

  const aggregates = useMemo(() => {
    // Exclude "floating" patients (LEFT-side rows whose scheduled meeting
    // passed without a status update — waiting_time <= 0) from avg/med/max.
    // They drag the stats toward 0 (or negative on signed data) even though
    // the typical upcoming meeting is days away. Total still counts them
    // (they're real patients in the bucket); only the time stats exclude them.
    const waits = filteredAll
      .filter((p) => !(side === 'left' && p.waiting_time <= 0))
      .map((p) => p.waiting_time);
    // Time IN THE STAGE — from the previous milestone, which is the number the main
    // screen puts on its tile. Both live here now because they answer different
    // questions and used to share one label: on 2026-09-28 the screen read 126 days
    // for psychiatrist intake and this drill read 8, and nothing said the clock had
    // changed.
    //
    // Nothing is excluded (Ohad, 2026-09-28). One psychiatrist-intake row reads
    // 20,743 days — 56 years, a mistyped appointment year — and on its own it lifts
    // that average from 111 to 125. Filtering it would have made the screen prettier
    // and quietly disagreed with the CSV, the table below it and the main screen's
    // tile. A visible absurd maximum is a bug report; a clean average is a cover-up.
    const stage = filteredAll
      .map((p) => p.days_in_stage)
      .filter((d): d is number => typeof d === 'number');
    return {
      total: filteredAll.length,
      avg: waits.length ? Math.round(waits.reduce((s, w) => s + w, 0) / waits.length) : 0,
      med: median(waits),
      max: waits.length ? Math.max(...waits) : 0,
      stageAvg: stage.length ? Math.round(stage.reduce((s, w) => s + w, 0) / stage.length) : 0,
      stageMed: median(stage),
      stageMax: stage.length ? Math.max(...stage) : 0,
      stageN: stage.length,
    };
  }, [filteredAll, side]);

  // Stacked histogram: rows are buckets, columns are clinics. `_total` lets the
  // top bar of each stack render a single combined-count label above the column.
  const histogram = useMemo(() => {
    return buckets.map((b) => {
      const row: Record<string, number | string> = { label: b.label };
      let total = 0;
      for (const clinic of clinicsInData) {
        const cfg = getClinicConfig(clinic);
        const count = dataForHistogram.filter(
          (p) => p.clinic_group === clinic && p.waiting_time >= b.min && p.waiting_time < b.max,
        ).length;
        row[cfg.displayName] = count;
        total += count;
      }
      row._total = total;
      return row;
    });
  }, [dataForHistogram, clinicsInData, buckets]);

  // Clinic panel — cross-filter: every filter except clinic. Always render all
  // clinics present in this drill (count=0 if filtered out), with .muted on
  // non-selected so the affordance to switch is preserved.
  const clinicBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    dataForClinic.forEach((p) => {
      counts[p.clinic_group] = (counts[p.clinic_group] ?? 0) + 1;
    });
    const total = dataForClinic.length;
    return clinicsInData
      .map((clinic) => ({
        clinic,
        count: counts[clinic] ?? 0,
        pct: total ? ((counts[clinic] ?? 0) / total) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [dataForClinic, clinicsInData]);

  // Provider breakdown — cross-filter (all except provider).
  // Empty values ("(טרם שויך)") are kept and sorted to the end as a state, not a name.
  const providerBreakdown = useMemo(() => {
    if (!providerColumn) return [];
    const counts: Record<string, number> = {};
    dataForProvider.forEach((p) => {
      const v = getProvider(p, providerColumn.field);
      counts[v] = (counts[v] ?? 0) + 1;
    });
    const total = dataForProvider.length;
    return Object.entries(counts)
      .map(([value, count]) => ({ value, count, pct: total ? (count / total) * 100 : 0 }))
      .sort((a, b) => {
        if (a.value === EMPTY_PROVIDER_LABEL) return 1;
        if (b.value === EMPTY_PROVIDER_LABEL) return -1;
        return b.count - a.count;
      });
  }, [dataForProvider, providerColumn]);

  // Invitation/status breakdown — cross-filter (all except status).
  const statusBreakdown = useMemo(() => {
    if (!statusColumn) return [];
    const counts: Record<string, number> = {};
    dataForStatus.forEach((p) => {
      const v = getRowStatus(p, statusColumn.field);
      counts[v] = (counts[v] ?? 0) + 1;
    });
    const total = dataForStatus.length;
    return Object.entries(counts)
      .map(([value, count]) => ({ value, count, pct: total ? (count / total) * 100 : 0 }))
      .sort((a, b) => {
        if (a.value === EMPTY_STATUS_LABEL) return 1;
        if (b.value === EMPTY_STATUS_LABEL) return -1;
        return b.count - a.count;
      });
  }, [dataForStatus, statusColumn]);


  // For the pills row at the top — show all clinics in palette order with raw counts (not affected by current selection)
  const pillData = useMemo<ClinicData[]>(() => {
    if (!data) return [];
    const counts: Record<string, number> = {};
    data.patients.forEach((p) => {
      counts[p.clinic_group] = (counts[p.clinic_group] ?? 0) + 1;
    });
    return clinicsInData.map((id) => ({
      id,
      name: id,
      count: counts[id] ?? 0,
      pct: data.patients.length ? ((counts[id] ?? 0) / data.patients.length) * 100 : 0,
    }));
  }, [data, clinicsInData]);

  // Per-bucket counts for the time-filter dropdown — cross-filter (all except bucket).
  const bucketCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    buckets.forEach((b) => {
      counts[b.label] = dataForHistogram.filter(
        (p) => p.waiting_time >= b.min && p.waiting_time < b.max,
      ).length;
    });
    return counts;
  }, [dataForHistogram, buckets]);

  // Distinct status values + counts (cross-filter, all except status).
  const statusOptions = useMemo<{ value: string; count: number }[]>(() => {
    if (!statusColumn) return [];
    const counts: Record<string, number> = {};
    dataForStatus.forEach((p) => {
      const v = getRowStatus(p, statusColumn.field);
      counts[v] = (counts[v] ?? 0) + 1;
    });
    return Object.entries(counts)
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count);
  }, [dataForStatus, statusColumn]);

  // Distinct provider names + counts (cross-filter, all except provider).
  // The empty bucket "(טרם שויך)" sinks to the bottom — it's a state, not a person.
  const providerOptions = useMemo<{ value: string; count: number }[]>(() => {
    if (!providerColumn) return [];
    const counts: Record<string, number> = {};
    dataForProvider.forEach((p) => {
      const v = getProvider(p, providerColumn.field);
      counts[v] = (counts[v] ?? 0) + 1;
    });
    return Object.entries(counts)
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => {
        if (a.value === EMPTY_PROVIDER_LABEL) return 1;
        if (b.value === EMPTY_PROVIDER_LABEL) return -1;
        return b.count - a.count;
      });
  }, [dataForProvider, providerColumn]);

  const filtered = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filteredAll].sort((a, b) => {
      switch (sortKey) {
        case 'waiting_time':
          return (a.waiting_time - b.waiting_time) * dir;
        case 'created_date':
          return a.created_date.localeCompare(b.created_date) * dir;
        case 'clinic_group':
          return (
            getClinicConfig(a.clinic_group).displayName.localeCompare(
              getClinicConfig(b.clinic_group).displayName, 'he',
            ) * dir
          );
        case 'hm_id':
          return a.hm_id.localeCompare(b.hm_id) * dir;
        case 'next_meeting_date': {
          // NULLs sort to the end regardless of direction (no meeting = no info)
          const av = a.next_meeting_date;
          const bv = b.next_meeting_date;
          if (!av && !bv) return 0;
          if (!av) return 1;
          if (!bv) return -1;
          return av.localeCompare(bv) * dir;
        }
        case 'provider': {
          if (!providerColumn) return 0;
          const av = getProvider(a, providerColumn.field);
          const bv = getProvider(b, providerColumn.field);
          // "(טרם שויך)" always sinks to the end — it's a missing assignment,
          // not a real name in the alphabet.
          if (av === EMPTY_PROVIDER_LABEL && bv === EMPTY_PROVIDER_LABEL) return 0;
          if (av === EMPTY_PROVIDER_LABEL) return 1;
          if (bv === EMPTY_PROVIDER_LABEL) return -1;
          return av.localeCompare(bv, 'he') * dir;
        }
        case 'status': {
          if (!statusColumn) return 0;
          const av = getRowStatus(a, statusColumn.field);
          const bv = getRowStatus(b, statusColumn.field);
          // "(ללא)" always sinks to the end — same logic.
          if (av === EMPTY_STATUS_LABEL && bv === EMPTY_STATUS_LABEL) return 0;
          if (av === EMPTY_STATUS_LABEL) return 1;
          if (bv === EMPTY_STATUS_LABEL) return -1;
          return av.localeCompare(bv, 'he') * dir;
        }
        default:
          return 0;
      }
    });
  }, [filteredAll, sortKey, sortDir, statusColumn, providerColumn]);

  // Flagged-count for the toggle pill label — cross-filter (all except red flag),
  // so the count tells the user "this is how many you'd see if you toggle the
  // flag on" / "how many of the current cross-section are flagged".
  const flaggedCount = useMemo(
    () => dataForRedFlag.filter((p) => p.red_flag).length,
    [dataForRedFlag],
  );


  const clinicFilterActive = !!selectedClinics && selectedClinics.size < clinicsInData.length;
  // "Filter active" is computed against the STABLE full sets — not the
  // cross-filtered options — so that selecting "all" after a reset reliably
  // reports inactive, even when the cross-filter has narrowed the options.
  const statusFilterActive =
    !!statusColumn && !!selectedStatuses && selectedStatuses.size < allStatusValuesInData.length;
  const providerFilterActive =
    !!providerColumn && !!selectedProviders && selectedProviders.size < allProvidersInData.length;
  const listGateOpen = showAllClicked || !!selectedBucket || clinicFilterActive || statusFilterActive || providerFilterActive || redFlagOnly;
  const anyFilterActive = !!selectedBucket || clinicFilterActive || statusFilterActive || providerFilterActive || redFlagOnly;

  // Reset to page 1 whenever the filtered list changes
  useEffect(() => {
    setPageIndex(0);
  }, [selectedClinics, selectedBucket, selectedStatuses, selectedProviders, redFlagOnly, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePageIndex = Math.min(pageIndex, pageCount - 1);
  const pageRows = useMemo(
    () => filtered.slice(safePageIndex * PAGE_SIZE, (safePageIndex + 1) * PAGE_SIZE),
    [filtered, safePageIndex],
  );

  const handlePillClick = (clinic: ClinicId) => {
    if (!selectedClinics) return;
    if (selectedClinics.size === 1 && selectedClinics.has(clinic)) {
      setSelectedClinics(new Set(clinicsInData));
    } else {
      setSelectedClinics(new Set([clinic]));
    }
  };

  const handleClinicBarClick = (clinic: ClinicId) => {
    handlePillClick(clinic);
  };

  // Click a provider bar → isolate to just that provider (or restore "all" if
  // already isolated). Mirrors handlePillClick for clinic bars — uses the
  // stable `allProvidersInData` so the reset always restores the full set,
  // regardless of how the cross-filter is currently narrowing other panels.
  const handleProviderBarClick = (value: string) => {
    if (!providerColumn) return;
    setSelectedProviders((prev) => {
      if (prev && prev.size === 1 && prev.has(value)) {
        return new Set(allProvidersInData);
      }
      return new Set([value]);
    });
    setShowAllClicked(true);
  };

  const handleStatusBarClick = (value: string) => {
    if (!statusColumn) return;
    setSelectedStatuses((prev) => {
      if (prev && prev.size === 1 && prev.has(value)) {
        return new Set(allStatusValuesInData);
      }
      return new Set([value]);
    });
    setShowAllClicked(true);
  };

  const handleSelectClinicFromDropdown = (clinic: ClinicId | 'all') => {
    if (clinic === 'all') {
      setSelectedClinics(new Set(clinicsInData));
      // explicit "show all" — keep the list open even after the filter is cleared
      setShowAllClicked(true);
      setOpenDropdown(null);
      return;
    }
    setSelectedClinics((prev) => {
      if (!prev) return new Set([clinic]);
      const allSelected = prev.size === clinicsInData.length;
      if (allSelected) {
        // first click while everything is selected — narrow down to just this clinic
        return new Set([clinic]);
      }
      // multi-select: toggle this clinic in/out of the current selection
      const next = new Set(prev);
      if (next.has(clinic)) next.delete(clinic);
      else next.add(clinic);
      return next;
    });
    // keep the dropdown open so the user can pick more clinics
  };

  const handleSelectBucketFromDropdown = (label: string | 'all') => {
    setSelectedBucket(label === 'all' ? null : label);
    if (label === 'all') setShowAllClicked(true);
    setOpenDropdown(null);
  };

  const handleClearFilters = () => {
    setSelectedClinics(new Set(clinicsInData));
    setSelectedBucket(null);
    if (statusColumn) {
      setSelectedStatuses(new Set(allStatusValuesInData));
    }
    if (providerColumn) {
      setSelectedProviders(new Set(allProvidersInData));
    }
    setRedFlagOnly(false);
    // Reset sort back to initial — "clear" means clean slate, including order.
    setSortKey('waiting_time');
    setSortDir('desc');
    setShowAllClicked(false);
    setOpenDropdown(null);
  };

  const handleSelectStatusFromDropdown = (value: string | 'all') => {
    if (!statusColumn) return;
    if (value === 'all') {
      setSelectedStatuses(new Set(allStatusValuesInData));
      setShowAllClicked(true);
      setOpenDropdown(null);
      return;
    }
    setSelectedStatuses((prev) => {
      if (!prev) return new Set([value]);
      const allSelected = prev.size === allStatusValuesInData.length;
      if (allSelected) return new Set([value]);
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const handleSelectProviderFromDropdown = (value: string | 'all') => {
    if (!providerColumn) return;
    if (value === 'all') {
      setSelectedProviders(new Set(allProvidersInData));
      setShowAllClicked(true);
      setOpenDropdown(null);
      return;
    }
    setSelectedProviders((prev) => {
      if (!prev) return new Set([value]);
      const allSelected = prev.size === allProvidersInData.length;
      if (allSelected) return new Set([value]);
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const handleExportCsv = () => {
    if (!data) return;
    const rows = filtered;
    const header = ['HM_ID', 'קופה', 'תאריך קליטה', data.time_label];
    if (showMeetingDate) header.push('תאריך פגישה');
    if (providerColumn) header.push(providerColumn.header);
    if (statusColumn) header.push(statusColumn.header);
    header.push('דגל אדום');
    const body = rows.map((p) => {
      const row: (string | number)[] = [
        p.hm_id,
        getClinicConfig(p.clinic_group).displayName,
        formatDateDmy(p.created_date),
        p.waiting_time,
      ];
      if (showMeetingDate) {
        row.push(p.next_meeting_date ? formatDateDmy(p.next_meeting_date) : '');
      }
      if (providerColumn) row.push(getProvider(p, providerColumn.field));
      if (statusColumn) row.push(getRowStatus(p, statusColumn.field));
      row.push(p.red_flag ? (p.red_flag_reasons.join(' · ') || 'כן') : '');
      return row;
    });
    const escape = (v: string | number) => {
      const s = String(v ?? '');
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [header, ...body].map((row) => row.map(escape).join(',')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `patients_${stage}_${side}_${today()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortArrow = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  // Pill button labels reflect current selection
  const clinicPillLabel = (() => {
    if (!clinicFilterActive || !selectedClinics) return 'סנן לפי קופה';
    if (selectedClinics.size === 1) {
      const only = Array.from(selectedClinics)[0];
      return `קופה: ${getClinicConfig(only).displayName}`;
    }
    return `קופה: ${selectedClinics.size} נבחרו`;
  })();
  const timePillLabel = selectedBucket
    ? `זמן: ${formatBucketLabel(selectedBucket)}`
    : 'סנן לפי זמן';
  const statusPillLabel = (() => {
    if (!statusColumn) return null;
    if (!statusFilterActive || !selectedStatuses) return `סנן לפי ${statusColumn.header}`;
    if (selectedStatuses.size === 1) {
      const only = Array.from(selectedStatuses)[0];
      return `${statusColumn.header}: ${only}`;
    }
    return `${statusColumn.header}: ${selectedStatuses.size} נבחרו`;
  })();
  const providerPillLabel = (() => {
    if (!providerColumn) return null;
    if (!providerFilterActive || !selectedProviders) return `סנן לפי ${providerColumn.header}`;
    if (selectedProviders.size === 1) {
      const only = Array.from(selectedProviders)[0];
      return `${providerColumn.header}: ${only}`;
    }
    return `${providerColumn.header}: ${selectedProviders.size} נבחרו`;
  })();

  if (error) {
    return (
      <Shell>
        <p className="drill-status">שגיאה: {error}</p>
        <button className="drill-back-btn" onClick={() => navigate('/patients-full')}>← חזור לדשבורד</button>
      </Shell>
    );
  }
  if (!data || !selectedClinics) {
    return (
      <Shell>
        <p className="drill-status">טוען...</p>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="drill-topbar">
        <button className="drill-back-btn" onClick={() => navigate('/patients-full')}>← חזור</button>
        <h2 className="drill-title">
          {data.stage_name} · {data.side_label} ·{' '}
          <span className="num-eng">{fmt(aggregates.total)}</span>
          {aggregates.total !== data.total && (
            <> מתוך <span className="num-eng">{fmt(data.total)}</span></>
          )}
        </h2>
        {/* Two rows, two clocks, each named after what it counts from. The top one is
            the number the main screen shows; the bottom one is time until the
            appointment. They differ by a factor of nine at psychiatrist intake. */}
        <div className="drill-stats-stack">
          {data.stage_time_label && aggregates.stageN > 0 && (
            <div className="drill-stats-group drill-stats-stage">
              <span className="drill-stats-caption">{data.stage_time_label}</span>
              <div className="drill-stats-inline">
                <Stat label="ממוצע" value={aggregates.stageAvg} suffix=" ימים" />
                <Stat label="חציון" value={aggregates.stageMed} suffix=" ימים" />
                <Stat label="מקס׳" value={aggregates.stageMax} suffix=" ימים" />
              </div>
            </div>
          )}
          <div className="drill-stats-group">
            <span className="drill-stats-caption">{data.time_label}</span>
            <div className="drill-stats-inline">
              <Stat label="ממוצע" value={aggregates.avg} suffix=" ימים" />
              <Stat label="חציון" value={aggregates.med} suffix=" ימים" />
              <Stat label="מקס׳" value={aggregates.max} suffix=" ימים" />
            </div>
          </div>
        </div>
      </div>

      <ClinicPills clinics={pillData} selected={selectedClinics} onClinicClick={handlePillClick} />

      <div className="drill-grid-2">
        <section className="drill-section">
          <h3 className="drill-section-title">התפלגות לפי {data.time_label}</h3>
          <div style={{ width: '100%', height: 192, cursor: 'pointer' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={histogram}
                margin={{ top: 24, right: 4, left: 4, bottom: 0 }}
                onClick={(state: any) => {
                  // activeLabel is set when the click hits a column. Toggle on
                  // re-click of the same bucket — same as the clinic bars
                  // pattern in "לפי קופה".
                  const label = state?.activeLabel;
                  if (typeof label !== 'string') return;
                  handleSelectBucketFromDropdown(selectedBucket === label ? 'all' : label);
                }}
              >
                <XAxis
                  dataKey="label"
                  tick={{ fill: '#64748B', fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(255,255,255,0.06)' }}
                  contentStyle={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    fontSize: 13,
                    padding: '8px 10px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                  }}
                  itemStyle={{ color: '#1e293b', padding: '2px 0' }}
                  labelFormatter={() => ''}
                />
                {clinicsInData.map((c, i) => {
                  const cfg = getClinicConfig(c);
                  const isTopBar = i === clinicsInData.length - 1;
                  return (
                    <Bar key={c} dataKey={cfg.displayName} stackId="a" fill={cfg.hex}>
                      {histogram.map((entry, idx) => (
                        <Cell
                          key={idx}
                          fillOpacity={selectedBucket && entry.label !== selectedBucket ? 0.25 : 1}
                        />
                      ))}
                      {isTopBar && (
                        <LabelList
                          dataKey="_total"
                          position="top"
                          fill="#f1f5f9"
                          fontSize={12}
                          fontWeight={700}
                          formatter={(v: number) => (v > 0 ? v : '')}
                        />
                      )}
                    </Bar>
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="drill-section">
          <h3 className="drill-section-title">לפי קופה</h3>
          <div className="clinic-bars">
            {clinicBreakdown.map(({ clinic, count, pct }) => {
              const cfg = getClinicConfig(clinic);
              const isSelected = selectedClinics?.has(clinic);
              const isMuted = clinicFilterActive && !isSelected;
              return (
                <div
                  className={`clinic-bar-row${isMuted ? ' muted' : ''}`}
                  key={clinic}
                  onClick={() => handleClinicBarClick(clinic)}
                  title="לחץ לסינון"
                >
                  <span className="clinic-bar-name">{cfg.displayName}</span>
                  <div className="clinic-bar-track">
                    <div className="clinic-bar-fill" style={{ width: `${pct}%`, background: cfg.hex }} />
                  </div>
                  <span className="clinic-bar-count num-eng">{fmt(count)}</span>
                  <span className="clinic-bar-pct num-eng">{Math.round(pct)}%</span>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {(providerColumn || statusColumn) && (
        <div
          className="drill-grid-2"
          style={{ gridTemplateColumns: providerColumn && statusColumn ? '1fr 1fr' : '1fr' }}
        >
          {providerColumn && (
            <section className="drill-section">
              <div className="drill-section-header">
                <h3 className="drill-section-title">לפי {providerColumn.header}</h3>
                <div className="drill-provider-toggle" role="group" aria-label="בחר תצוגת מטפל">
                  <button
                    type="button"
                    className={`drill-provider-toggle-btn${selectedProviderView === 'cm' ? ' active' : ''}`}
                    onClick={() => handleSwitchProviderView('cm')}
                  >
                    מנהל תיק
                  </button>
                  <button
                    type="button"
                    className={`drill-provider-toggle-btn${selectedProviderView === 'psy' ? ' active' : ''}`}
                    onClick={() => handleSwitchProviderView('psy')}
                  >
                    פסיכיאטר
                  </button>
                </div>
              </div>
              <div className="clinic-bars drill-bars-scroll">
                {providerBreakdown.map(({ value, count, pct }) => {
                  const isSelected = providerFilterActive && selectedProviders?.has(value);
                  const isMuted = providerFilterActive && !isSelected;
                  const isEmpty = value === EMPTY_PROVIDER_LABEL;
                  return (
                    <div
                      className={`clinic-bar-row${isMuted ? ' muted' : ''}`}
                      key={value}
                      onClick={() => handleProviderBarClick(value)}
                      title="לחץ לסינון"
                    >
                      <span className="clinic-bar-name" style={isEmpty ? { color: '#64748B' } : undefined}>{value}</span>
                      <div className="clinic-bar-track">
                        <div className="clinic-bar-fill" style={{ width: `${pct}%`, background: '#06b6d4' }} />
                      </div>
                      <span className="clinic-bar-count num-eng">{fmt(count)}</span>
                      <span className="clinic-bar-pct num-eng">{Math.round(pct)}%</span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {statusColumn && (
            <section className="drill-section">
              <h3 className="drill-section-title">לפי {statusColumn.header}</h3>
              <div className="clinic-bars drill-bars-scroll">
                {statusBreakdown.map(({ value, count, pct }) => {
                  const isSelected = statusFilterActive && selectedStatuses?.has(value);
                  const isMuted = statusFilterActive && !isSelected;
                  const isEmpty = value === EMPTY_STATUS_LABEL;
                  return (
                    <div
                      className={`drill-status-row${isMuted ? ' muted' : ''}`}
                      key={value}
                      onClick={() => handleStatusBarClick(value)}
                      title="לחץ לסינון"
                    >
                      <span className="drill-status-row-name" style={isEmpty ? { color: '#64748B' } : undefined}>{value}</span>
                      <div className="drill-status-row-meter">
                        <div className="clinic-bar-track">
                          <div className="clinic-bar-fill" style={{ width: `${pct}%`, background: '#a855f7' }} />
                        </div>
                        <span className="clinic-bar-count num-eng">{fmt(count)}</span>
                        <span className="clinic-bar-pct num-eng">{Math.round(pct)}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      )}

      <section className="drill-section">
        <div className="drill-filter-bar" ref={filterBarRef}>
          <button
            type="button"
            className={`drill-filter-pill${showAllClicked && !anyFilterActive ? ' active' : ''}`}
            onClick={() => setShowAllClicked(true)}
          >
            <span>הצג מטופלים</span>
          </button>

          <button
            type="button"
            className={`drill-filter-pill drill-red-flag-pill${redFlagOnly ? ' active' : ''}`}
            onClick={() => setRedFlagOnly((v) => !v)}
            disabled={flaggedCount === 0}
            title={flaggedCount === 0 ? 'אין מטופלים מסומנים בקופות שנבחרו' : 'הצג רק מטופלים עם דגל אדום'}
          >
            <span>🚩 דגל אדום (<span className="num-eng">{fmt(flaggedCount)}</span>)</span>
          </button>

          <div className="drill-filter-pill-wrap">
            <button
              type="button"
              className={`drill-filter-pill${clinicFilterActive ? ' active' : ''}${openDropdown === 'clinic' ? ' open' : ''}`}
              onClick={() => setOpenDropdown((d) => (d === 'clinic' ? null : 'clinic'))}
            >
              <span>{clinicPillLabel}</span>
              <span className="drill-filter-caret">▾</span>
            </button>
            {openDropdown === 'clinic' && (
              <div className="drill-dropdown">
                <button type="button" className="drill-dropdown-row" onClick={() => handleSelectClinicFromDropdown('all')}>
                  <span className="drill-dropdown-name">כל הקופות</span>
                  <span className="num-eng drill-dropdown-count">{fmt(dataForClinic.length)}</span>
                </button>
                {clinicsInData.map((c) => {
                  const cfg = getClinicConfig(c);
                  const count = dataForClinic.filter((p) => p.clinic_group === c).length;
                  const isSelected = clinicFilterActive && selectedClinics?.has(c);
                  return (
                    <button
                      type="button"
                      key={c}
                      className={`drill-dropdown-row${isSelected ? ' selected' : ''}`}
                      onClick={() => handleSelectClinicFromDropdown(c)}
                    >
                      <span className="pill-dot" style={{ background: cfg.hex }} />
                      <span className="drill-dropdown-name">{cfg.displayName}</span>
                      <span className="num-eng drill-dropdown-count">{fmt(count)}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="drill-filter-pill-wrap">
            <button
              type="button"
              className={`drill-filter-pill${selectedBucket ? ' active' : ''}${openDropdown === 'time' ? ' open' : ''}`}
              onClick={() => setOpenDropdown((d) => (d === 'time' ? null : 'time'))}
            >
              <span>{timePillLabel}</span>
              <span className="drill-filter-caret">▾</span>
            </button>
            {openDropdown === 'time' && (
              <div className="drill-dropdown">
                <button type="button" className="drill-dropdown-row" onClick={() => handleSelectBucketFromDropdown('all')}>
                  <span className="drill-dropdown-name">כל הזמנים</span>
                  <span className="num-eng drill-dropdown-count">{fmt(dataForHistogram.length)}</span>
                </button>
                {buckets.map((b) => (
                  <button
                    type="button"
                    key={b.label}
                    className={`drill-dropdown-row${selectedBucket === b.label ? ' selected' : ''}`}
                    onClick={() => handleSelectBucketFromDropdown(b.label)}
                  >
                    <span className="drill-dropdown-name num-eng">{formatBucketLabel(b.label)}</span>
                    <span className="num-eng drill-dropdown-count">{fmt(bucketCounts[b.label] ?? 0)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {statusColumn && (
            <div className="drill-filter-pill-wrap">
              <button
                type="button"
                className={`drill-filter-pill${statusFilterActive ? ' active' : ''}${openDropdown === 'status' ? ' open' : ''}`}
                onClick={() => setOpenDropdown((d) => (d === 'status' ? null : 'status'))}
              >
                <span>{statusPillLabel}</span>
                <span className="drill-filter-caret">▾</span>
              </button>
              {openDropdown === 'status' && (
                <div className="drill-dropdown">
                  <button
                    type="button"
                    className="drill-dropdown-row"
                    onClick={() => handleSelectStatusFromDropdown('all')}
                  >
                    <span className="drill-dropdown-name">{`כל ה${statusColumn.header}`}</span>
                    <span className="num-eng drill-dropdown-count">{fmt(dataForStatus.length)}</span>
                  </button>
                  {statusOptions.map((opt) => {
                    const isSelected = statusFilterActive && selectedStatuses?.has(opt.value);
                    return (
                      <button
                        type="button"
                        key={opt.value}
                        className={`drill-dropdown-row${isSelected ? ' selected' : ''}`}
                        onClick={() => handleSelectStatusFromDropdown(opt.value)}
                      >
                        <span className="drill-dropdown-name num-eng">{opt.value}</span>
                        <span className="num-eng drill-dropdown-count">{fmt(opt.count)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {providerColumn && (
            <div className="drill-filter-pill-wrap">
              <button
                type="button"
                className={`drill-filter-pill${providerFilterActive ? ' active' : ''}${openDropdown === 'provider' ? ' open' : ''}`}
                onClick={() => setOpenDropdown((d) => (d === 'provider' ? null : 'provider'))}
              >
                <span>{providerPillLabel}</span>
                <span className="drill-filter-caret">▾</span>
              </button>
              {openDropdown === 'provider' && (
                <div className="drill-dropdown">
                  <button
                    type="button"
                    className="drill-dropdown-row"
                    onClick={() => handleSelectProviderFromDropdown('all')}
                  >
                    <span className="drill-dropdown-name">{`כל ה${providerColumn.header}`}</span>
                    <span className="num-eng drill-dropdown-count">{fmt(dataForProvider.length)}</span>
                  </button>
                  {providerOptions.map((opt) => {
                    const isSelected = providerFilterActive && selectedProviders?.has(opt.value);
                    return (
                      <button
                        type="button"
                        key={opt.value}
                        className={`drill-dropdown-row${isSelected ? ' selected' : ''}`}
                        onClick={() => handleSelectProviderFromDropdown(opt.value)}
                      >
                        <span className="drill-dropdown-name num-eng">{opt.value}</span>
                        <span className="num-eng drill-dropdown-count">{fmt(opt.count)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            className="drill-filter-pill drill-clear-pill"
            onClick={handleClearFilters}
            disabled={!listGateOpen}
          >
            <span>נקה מסננים</span>
          </button>

          <button
            type="button"
            className="drill-filter-pill drill-csv-pill"
            onClick={handleExportCsv}
            disabled={!listGateOpen || filtered.length === 0}
          >
            <span>📥 ייצא CSV</span>
          </button>
        </div>

        {listGateOpen ? (
          <>
            <div className="drill-list-total">
              סה״כ <span className="num-eng">{fmt(filtered.length)}</span> מטופלים
              {anyFilterActive && (
                <span className="drill-list-total-sub"> · מתוך <span className="num-eng">{fmt(data.total)}</span></span>
              )}
            </div>
            <div className="drill-table-scroll">
              <table className="drill-table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleSort('hm_id')}>HM_ID{sortArrow('hm_id')}</th>
                    <th className="sortable" onClick={() => handleSort('clinic_group')}>קופה{sortArrow('clinic_group')}</th>
                    <th className="sortable" onClick={() => handleSort('created_date')}>תאריך קליטה{sortArrow('created_date')}</th>
                    <th className="sortable" onClick={() => handleSort('waiting_time')}>{data.time_label}{sortArrow('waiting_time')}</th>
                    {showMeetingDate && (
                      <th className="sortable" onClick={() => handleSort('next_meeting_date')}>תאריך פגישה{sortArrow('next_meeting_date')}</th>
                    )}
                    {providerColumn && (
                      <th className="sortable" onClick={() => handleSort('provider')}>
                        {providerColumn.header}{sortArrow('provider')}
                      </th>
                    )}
                    {statusColumn && (
                      <th className="sortable" onClick={() => handleSort('status')}>
                        {statusColumn.header}{sortArrow('status')}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((p, i) => {
                    const meetingOverdue = !!p.next_meeting_date && p.next_meeting_date < todayIso;
                    return (
                    <tr key={p.hm_id || i}>
                      <td>
                        {p.red_flag && (
                          <>
                            <span
                              className="drill-flag-icon"
                              title={p.red_flag_reasons.length ? p.red_flag_reasons.join(' · ') : 'דגל אדום'}
                              aria-label="מטופל מסומן בדגל אדום"
                            >🚩</span>
                            {' '}
                          </>
                        )}
                        <PatientCardLink
                          linked={canOpenPatient}
                          href={`${CLINICUI_PATIENT_URL}?id=${p.internal_id}`}
                          className={canOpenPatient ? 'drill-hm-link num-eng' : 'num-eng'}
                          title="פתח את המטופל ב-Clinic UI"
                        >
                          {p.hm_id}
                        </PatientCardLink>
                      </td>
                      <td>
                        <span className="clinic-tag" style={{ background: getClinicConfig(p.clinic_group).pillBg, borderColor: getClinicConfig(p.clinic_group).pillBorder }}>
                          <span className="pill-dot" style={{ background: getClinicConfig(p.clinic_group).hex }} />
                          {getClinicConfig(p.clinic_group).displayName}
                        </span>
                      </td>
                      <td><span className="num-eng">{formatDateDmy(p.created_date)}</span></td>
                      <td>
                        <span className={`num-eng${p.waiting_time < 0 ? ' drill-time-overdue' : ''}`}>
                          {fmt(p.waiting_time)}
                        </span>
                      </td>
                      {showMeetingDate && (
                        <td>
                          {p.next_meeting_date ? (
                            <span className={`drill-meeting-date${meetingOverdue ? ' overdue' : ''}`}>
                              <span className="num-eng">{formatDateDmy(p.next_meeting_date)}</span>
                              {meetingOverdue && <span className="drill-meeting-overdue-tag"> (איחור)</span>}
                            </span>
                          ) : (
                            <span style={{ color: '#64748b' }}>—</span>
                          )}
                        </td>
                      )}
                      {providerColumn && (() => {
                        const v = getProvider(p, providerColumn.field);
                        const isEmpty = v === EMPTY_PROVIDER_LABEL;
                        return (
                          <td>
                            <span className="num-eng" style={isEmpty ? { color: '#64748b' } : undefined}>{v}</span>
                          </td>
                        );
                      })()}
                      {statusColumn && (
                        <td><span className="num-eng">{getRowStatus(p, statusColumn.field)}</span></td>
                      )}
                    </tr>
                  );
                  })}
                  {pageRows.length === 0 && (
                    <tr><td colSpan={4 + (showMeetingDate ? 1 : 0) + (providerColumn ? 1 : 0) + (statusColumn ? 1 : 0)} style={{ textAlign: 'center', color: '#64748B', padding: '14px' }}>אין תוצאות בפילטר הנוכחי</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {filtered.length > PAGE_SIZE && (
              <div className="drill-pagination">
                <button
                  type="button"
                  className="drill-page-btn"
                  onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
                  disabled={safePageIndex === 0}
                >‹ הקודם</button>
                <span className="drill-page-info">
                  עמוד <span className="num-eng">{safePageIndex + 1}</span> מתוך <span className="num-eng">{pageCount}</span>
                </span>
                <button
                  type="button"
                  className="drill-page-btn"
                  onClick={() => setPageIndex((i) => Math.min(pageCount - 1, i + 1))}
                  disabled={safePageIndex >= pageCount - 1}
                >הבא ›</button>
              </div>
            )}
          </>
        ) : (
          <p className="drill-empty-hint">לחץ "הצג מטופלים" או בחר סינון כדי להציג את הרשימה</p>
        )}
      </section>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 overflow-y-auto p-6">
          <div className="drill-page patient-status-widget">{children}</div>
        </main>
      </div>
    </div>
  );
}

function Stat({ label, value, suffix }: { label: string; value: number; suffix?: string }) {
  return (
    <div className="drill-stat">
      <span className="drill-stat-label">{label}</span>
      <span className="drill-stat-value num-eng">
        {fmt(value)}
        {suffix ?? ''}
      </span>
    </div>
  );
}
